import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ExternalLink, CheckCircle2, AlertTriangle, ClipboardCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import ContentRequired from "@/components/ContentRequired";
import SurveyAdminPanel from "@/components/SurveyAdminPanel";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// kind: "opening" | "exit"
export default function SurveyPage({ kind }) {
  const { user, refreshMe } = useAuth();
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "faculty";

  const loadMeta = () => api.get("/programme").then(({ data }) => setMeta(data || {}));
  useEffect(() => { loadMeta(); }, []);

  const url = kind === "opening" ? meta.opening_survey_url : meta.exit_survey_url;
  const label = (kind === "opening" ? meta.opening_survey_label : meta.exit_survey_label) ||
    (kind === "opening" ? "Opening survey" : "Exit survey");
  const completed = kind === "opening"
    ? !!user?.opening_survey_completed
    : !!user?.exit_survey_completed;
  const completedAt = kind === "opening" ? user?.opening_survey_completed_at : user?.exit_survey_completed_at;

  const toggle = async (next) => {
    setBusy(true);
    try {
      await api.post("/users/me/surveys",
        kind === "opening" ? { opening_completed: next } : { exit_completed: next });
      await refreshMe();
      toast.success(next ? "Marked as completed" : "Marked as not completed");
    } catch (e) {
      toast.error("Could not save");
    } finally { setBusy(false); }
  };

  const title = kind === "opening"
    ? "Opening survey — before you start"
    : "Exit survey — your final reflection";
  const blurb = kind === "opening"
    ? "Tell us where you're starting from. The opening survey takes 5 minutes and helps the programme team shape the sessions around you."
    : "We'd love your final reflection on the programme. Please complete the exit survey on pitch day before you leave.";

  return (
    <div className="space-y-8" data-testid={`survey-${kind}-page`}>
      <div>
        <div className="label-eyebrow flex items-center gap-2">
          <ClipboardCheck size={12} /> {kind === "opening" ? "Opening" : "Exit"} survey
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">{title}</h1>
        <p className="text-[#525252] mt-2 max-w-2xl text-sm">{blurb}</p>
      </div>

      {isAdmin && <SurveyAdminPanel key={`${meta.updated_at}-${kind}`} kind={kind} meta={meta} onSaved={loadMeta} />}

      {/* Status banner */}
      {!url ? (
        isAdmin ? null : (
        <ContentRequired
          title={`${label} link not set yet`}
          description="The programme team hasn't added the survey link yet — check back soon."
          needed="Survey URL"
          where={`${label} page`}
          format="Public URL"
          testid={`survey-${kind}-content-required`}
        />)
      ) : completed ? (
        <div className="border-2 border-[#2596be] bg-[#E6F4FA] p-5 flex items-start gap-4" data-testid={`survey-${kind}-completed-banner`}>
          <CheckCircle2 size={24} className="text-[#2596be] shrink-0 mt-0.5" strokeWidth={1.7} />
          <div className="flex-1">
            <div className="label-eyebrow text-[#1e7da3]">Completed — thank you</div>
            <div className="font-display font-bold text-lg tracking-tight mt-1">{label}</div>
            {completedAt && <div className="text-xs text-[#525252] mt-1">Marked completed {completedAt.slice(0, 16).replace("T", " ")}</div>}
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-[#f4b847] bg-[#FEF3D0] p-5 flex items-start gap-4" data-testid={`survey-${kind}-reminder-banner`}>
          <AlertTriangle size={24} className="text-[#7A5B17] shrink-0 mt-0.5" strokeWidth={1.7} />
          <div className="flex-1">
            <div className="label-eyebrow text-[#7A5B17]">Reminder</div>
            <div className="font-display font-bold text-lg tracking-tight mt-1">
              {kind === "opening" ? "Complete the Opening Survey." : "Complete the Exit Survey before you leave."}
            </div>
            <div className="text-sm text-[#525252] mt-1">Click the link, or scan the QR code with your phone or tablet. When you've submitted it, come back and flip the toggle below.</div>
          </div>
        </div>
      )}

      {url && (
        <div className="border border-[#0A0A0A] bg-white p-6 space-y-5">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
            <div>
              <div className="label-eyebrow">Survey link</div>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid={`survey-${kind}-open-link`}
                className="mt-3 inline-flex items-center gap-2 bg-[#2596be] hover:bg-[#1e7da3] text-white px-5 py-3 font-semibold text-sm uppercase tracking-wider transition-colors"
              >
                <ExternalLink size={16} /> Open {label.toLowerCase()}
              </a>
              <p className="text-xs text-[#525252] mt-3 break-all max-w-xl">{url}</p>
            </div>
            {/* QR for phone use — useful in-class */}
            <div className="text-center" data-testid={`survey-${kind}-qr`}>
              <div className="border border-[#0A0A0A] bg-white p-2 inline-block">
                <img
                  src={`${API_BASE}/api/surveys/${kind}/qr.png?v=${encodeURIComponent(meta[`${kind}_survey_qr_path`] || meta.updated_at || "")}`}
                  alt={`QR code for ${label}`}
                  className="w-40 h-40 object-contain"
                  data-testid={`survey-${kind}-qr-img`}
                />
              </div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#525252] mt-2">Scan with your phone or tablet</div>
            </div>
          </div>

          <div className="border-t border-[#E5E7EB] pt-5 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <Label className="label-eyebrow">Mark as completed</Label>
              <p className="text-xs text-[#525252] mt-1 max-w-md">
                Flip this on once you've submitted the survey. Faculty can see overall completion rates but not your individual answers (those stay in the survey platform).
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium" data-testid={`survey-${kind}-toggle-label`}>
                {completed ? "Completed" : "Not completed"}
              </span>
              <Switch
                checked={completed}
                onCheckedChange={toggle}
                disabled={busy}
                data-testid={`survey-${kind}-toggle`}
                className="data-[state=checked]:bg-[#2596be]"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
